// Collection and featured-collection videos adapted from La Mere de Famille.
(function() {
  if (!window.IntersectionObserver) return;

  const TILE_SELECTOR = ".collection-grid-tile[data-collection-tile-video-mode]";
  const TEMPLATE_SELECTOR = "template[data-collection-grid-tile-video-template]";
  const VIDEO_SELECTOR = ".collection-grid-tile__video";
  const reducedMotionMedia = window.matchMedia("(prefers-reduced-motion: reduce)");
  const observedTiles = new Set();
  const visibleTiles = new Set();
  let resizeFrame = null;

  const isTileVideoEnabled = (tile) => {
    const mode = tile.dataset.collectionTileVideoMode;
    const breakpoint = Number.parseInt(tile.dataset.collectionTileDesktopBreakpoint, 10);

    if (reducedMotionMedia.matches) return false;
    if (mode === "mobile") return window.matchMedia("(max-width: 1199px)").matches;

    return mode === "desktop"
      && Number.isFinite(breakpoint)
      && window.matchMedia(`(min-width: ${breakpoint}px)`).matches;
  };

  const getVideo = (tile) => tile.querySelector(VIDEO_SELECTOR);

  const stopVideo = (tile, reset = false) => {
    tile.classList.remove("is-video-playing");
    const video = getVideo(tile);
    if (!video) return;

    video.pause();

    if (reset && video.readyState > 0) {
      try {
        video.currentTime = 0;
      } catch (error) {
        // The metadata may not be available yet.
      }
    }
  };

  const unloadVideo = (tile) => {
    stopVideo(tile, true);
    const video = getVideo(tile);
    if (video) video.remove();
  };

  const ensureVideo = (tile) => {
    const existingVideo = getVideo(tile);
    if (existingVideo) return existingVideo;

    const template = tile.querySelector(TEMPLATE_SELECTOR);
    if (!template) return null;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector("video");
    if (!video) return null;

    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;

    video.addEventListener("playing", () => {
      if (isTileVideoEnabled(tile) && visibleTiles.has(tile) && !document.hidden) {
        tile.classList.add("is-video-playing");
      } else {
        video.pause();
      }
    });
    video.addEventListener("error", () => {
      tile.classList.remove("is-video-playing");
    });

    template.before(fragment);
    video.load();

    return video;
  };

  const playVideo = (tile) => {
    if (!isTileVideoEnabled(tile) || !visibleTiles.has(tile) || document.hidden) return;

    const video = ensureVideo(tile);
    if (!video) return;

    const playPromise = video.play();
    if (playPromise) {
      playPromise.catch(() => {
        tile.classList.remove("is-video-playing");
      });
    }
  };

  const syncTile = (tile) => {
    if (!tile.isConnected) {
      observedTiles.delete(tile);
      visibleTiles.delete(tile);
      return;
    }

    if (!isTileVideoEnabled(tile)) {
      unloadVideo(tile);
      return;
    }

    if (visibleTiles.has(tile) && !document.hidden) {
      playVideo(tile);
    } else {
      stopVideo(tile, true);
    }
  };

  const syncTiles = () => {
    observedTiles.forEach(syncTile);
  };

  const intersectionObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        visibleTiles.add(entry.target);
      } else {
        visibleTiles.delete(entry.target);
      }

      syncTile(entry.target);
    });
  }, { threshold: 0.01 });

  const observeTiles = (root) => {
    const tiles = [];

    if (root.nodeType === Node.ELEMENT_NODE && root.matches(TILE_SELECTOR)) {
      tiles.push(root);
    }

    if (typeof root.querySelectorAll === "function") {
      tiles.push(...root.querySelectorAll(TILE_SELECTOR));
    }

    tiles.forEach((tile) => {
      if (observedTiles.has(tile) || !tile.querySelector(TEMPLATE_SELECTOR)) return;

      observedTiles.add(tile);
      intersectionObserver.observe(tile);
    });
  };

  const forgetTiles = (root) => {
    const tiles = [];

    if (root.nodeType === Node.ELEMENT_NODE && root.matches(TILE_SELECTOR)) {
      tiles.push(root);
    }

    if (typeof root.querySelectorAll === "function") {
      tiles.push(...root.querySelectorAll(TILE_SELECTOR));
    }

    tiles.forEach((tile) => {
      if (tile.isConnected) return;
      stopVideo(tile, true);
      intersectionObserver.unobserve(tile);
      observedTiles.delete(tile);
      visibleTiles.delete(tile);
    });
  };

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach(observeTiles);
      mutation.removedNodes.forEach(forgetTiles);
    });
  });

  observeTiles(document);
  mutationObserver.observe(document.body, { childList: true, subtree: true });

  window.addEventListener("resize", () => {
    if (resizeFrame !== null) return;

    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = null;
      syncTiles();
    });
  });
  document.addEventListener("visibilitychange", syncTiles);
  document.addEventListener("theme:loading:start", () => {
    observedTiles.forEach((tile) => stopVideo(tile));
  });
  document.addEventListener("theme:loading:end", () => {
    observeTiles(document);
    syncTiles();
  });

  if (typeof reducedMotionMedia.addEventListener === "function") {
    reducedMotionMedia.addEventListener("change", syncTiles);
  } else if (typeof reducedMotionMedia.addListener === "function") {
    reducedMotionMedia.addListener(syncTiles);
  }
})();

(function() {
  if (!window.IntersectionObserver) return;

  const HOVER_DELAY = 100;
  const HOVER_VIDEO_QUERY = "(min-width: 1000px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)";
  const HOVER_VIDEO_SELECTOR = ".product-item__image-wrapper--hover-video";
  const AUTOPLAY_VIDEO_SELECTOR = ".product-item__image-wrapper--autoplay-video";
  const hoverVideoMedia = window.matchMedia(HOVER_VIDEO_QUERY);
  let hoverTimer = null;
  let pendingWrapper = null;
  let activeWrapper = null;
  let autoplayObserver = null;
  const visibleAutoplayWrappers = new Set();

  const clearPendingHover = () => {
    if (hoverTimer !== null) {
      window.clearTimeout(hoverTimer);
    }

    hoverTimer = null;
    pendingWrapper = null;
  };

  const getHoverVideo = (wrapper) => wrapper.querySelector(".product-item__hover-video");
  const isAutoplayWrapper = (wrapper) => wrapper.matches(AUTOPLAY_VIDEO_SELECTOR);

  const isPlaybackAllowed = (wrapper) => {
    if (!hoverVideoMedia.matches || document.hidden || !wrapper.isConnected) return false;
    if (wrapper.closest("[hidden]")) return false;

    if (isAutoplayWrapper(wrapper)) {
      return visibleAutoplayWrappers.has(wrapper) && !wrapper.classList.contains("is-autoplay-video-image-hover");
    }

    return activeWrapper === wrapper && wrapper.matches(":hover");
  };

  const ensureHoverVideo = (wrapper) => {
    const existingVideo = getHoverVideo(wrapper);
    if (existingVideo) return existingVideo;

    const template = wrapper.querySelector("template[data-product-hover-video-template]");
    if (!template) return null;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector("video");
    if (!video) return null;

    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;

    video.addEventListener("playing", () => {
      if (isPlaybackAllowed(wrapper)) {
        wrapper.classList.add("is-hover-video-playing");
        if (isAutoplayWrapper(wrapper)) wrapper.classList.add("is-autoplay-video-active");
        return;
      }

      video.pause();
    });

    video.addEventListener("error", () => stopHoverVideo(wrapper));
    template.replaceWith(fragment);
    video.load();

    return video;
  };

  const stopHoverVideo = (wrapper) => {
    if (!wrapper) return;

    wrapper.classList.remove("is-hover-video-playing");
    wrapper.classList.remove("is-autoplay-video-active");
    wrapper.classList.remove("is-autoplay-video-image-hover");
    const video = getHoverVideo(wrapper);

    if (video) {
      video.pause();

      if (video.readyState > 0) {
        try {
          video.currentTime = 0;
        } catch (error) {
          // The video metadata may not be available yet.
        }
      }
    }

    if (activeWrapper === wrapper) {
      activeWrapper = null;
    }
  };

  const playHoverVideo = (wrapper) => {
    if (!isPlaybackAllowed(wrapper)) return;

    const video = ensureHoverVideo(wrapper);
    if (!video) return;

    const playPromise = video.play();

    if (playPromise) {
      playPromise.catch(() => {
        stopHoverVideo(wrapper);
      });
    }
  };

  const startHoverVideo = (wrapper) => {
    if (!hoverVideoMedia.matches || !wrapper.isConnected || !wrapper.matches(":hover") || isAutoplayWrapper(wrapper)) return;

    if (activeWrapper && activeWrapper !== wrapper) {
      stopHoverVideo(activeWrapper);
    }

    activeWrapper = wrapper;
    playHoverVideo(wrapper);
  };

  const showAutoplayImage = (wrapper) => {
    wrapper.classList.add("is-autoplay-video-image-hover");
    wrapper.classList.remove("is-autoplay-video-active");
    wrapper.classList.remove("is-hover-video-playing");

    const video = getHoverVideo(wrapper);
    if (video) video.pause();
  };

  const hideAutoplayImage = (wrapper) => {
    wrapper.classList.remove("is-autoplay-video-image-hover");
    playHoverVideo(wrapper);
  };

  const handlePointerOver = (event) => {
    if (!hoverVideoMedia.matches) return;

    const wrapper = event.target.closest(HOVER_VIDEO_SELECTOR);
    if (!wrapper || wrapper.contains(event.relatedTarget)) return;

    if (isAutoplayWrapper(wrapper)) {
      showAutoplayImage(wrapper);
      return;
    }

    clearPendingHover();
    pendingWrapper = wrapper;
    hoverTimer = window.setTimeout(() => {
      hoverTimer = null;
      pendingWrapper = null;
      startHoverVideo(wrapper);
    }, HOVER_DELAY);
  };

  const handlePointerOut = (event) => {
    const wrapper = event.target.closest(HOVER_VIDEO_SELECTOR);
    if (!wrapper || wrapper.contains(event.relatedTarget)) return;

    if (isAutoplayWrapper(wrapper)) {
      hideAutoplayImage(wrapper);
      return;
    }

    if (pendingWrapper === wrapper) {
      clearPendingHover();
    }

    stopHoverVideo(wrapper);
  };

  const stopActiveHoverVideo = () => {
    clearPendingHover();
    stopHoverVideo(activeWrapper);
  };

  const observeAutoplayWrappers = (root) => {
    if (!autoplayObserver) return;

    if (root.nodeType === Node.ELEMENT_NODE && root.matches(AUTOPLAY_VIDEO_SELECTOR)) {
      autoplayObserver.observe(root);
    }

    if (typeof root.querySelectorAll === "function") {
      root.querySelectorAll(AUTOPLAY_VIDEO_SELECTOR).forEach((wrapper) => {
        autoplayObserver.observe(wrapper);
      });
    }
  };

  const forgetHoverVideoWrappers = (root) => {
    const wrappers = [];

    if (root.nodeType === Node.ELEMENT_NODE && root.matches(HOVER_VIDEO_SELECTOR)) {
      wrappers.push(root);
    }

    if (typeof root.querySelectorAll === "function") {
      wrappers.push(...root.querySelectorAll(HOVER_VIDEO_SELECTOR));
    }

    wrappers.forEach((wrapper) => {
      if (wrapper.isConnected && wrapper.ownerDocument === document) return;

      if (pendingWrapper === wrapper) {
        clearPendingHover();
      }

      if (autoplayObserver && isAutoplayWrapper(wrapper)) {
        autoplayObserver.unobserve(wrapper);
      }

      visibleAutoplayWrappers.delete(wrapper);
      stopHoverVideo(wrapper);
    });
  };

  const stopAutoplayVideos = (clearVisibleWrappers) => {
    visibleAutoplayWrappers.forEach((wrapper) => {
      stopHoverVideo(wrapper);
    });

    if (clearVisibleWrappers) {
      visibleAutoplayWrappers.clear();
    }
  };

  const setupAutoplayObserver = () => {
    if (!hoverVideoMedia.matches || autoplayObserver) return;

    autoplayObserver = new IntersectionObserver((entries, observer) => {
      if (observer !== autoplayObserver || !hoverVideoMedia.matches) return;

      entries.forEach((entry) => {
        const wrapper = entry.target;

        if (!wrapper.isConnected || wrapper.ownerDocument !== document) {
          observer.unobserve(wrapper);
          visibleAutoplayWrappers.delete(wrapper);
          stopHoverVideo(wrapper);
          return;
        }

        if (entry.isIntersecting) {
          visibleAutoplayWrappers.add(wrapper);

          if (wrapper.matches(":hover")) {
            showAutoplayImage(wrapper);
          } else {
            playHoverVideo(wrapper);
          }

          return;
        }

        visibleAutoplayWrappers.delete(wrapper);
        stopHoverVideo(wrapper);
      });
    }, { threshold: 0.01 });

    observeAutoplayWrappers(document);
  };

  const teardownAutoplayObserver = () => {
    if (autoplayObserver) {
      autoplayObserver.disconnect();
      autoplayObserver = null;
    }

    stopAutoplayVideos(true);
  };

  const resumeAutoplayVideos = () => {
    if (!hoverVideoMedia.matches || document.hidden) return;

    visibleAutoplayWrappers.forEach((wrapper) => {
      if (wrapper.isConnected) {
        if (wrapper.matches(":hover")) {
          showAutoplayImage(wrapper);
        } else {
          playHoverVideo(wrapper);
        }
      } else {
        visibleAutoplayWrappers.delete(wrapper);
      }
    });
  };

  const autoplayMutationObserver = new MutationObserver((mutations) => {
    const removedRoots = [];

    mutations.forEach((mutation) => {
      // Featured collection tabs use hidden. Stop immediately, including pending
      // hover and late playing events, before IntersectionObserver catches up.
      if (mutation.type === "attributes" && mutation.target.hidden) {
        mutation.target.querySelectorAll(HOVER_VIDEO_SELECTOR).forEach((wrapper) => {
          if (pendingWrapper === wrapper) clearPendingHover();
          visibleAutoplayWrappers.delete(wrapper);
          stopHoverVideo(wrapper);
        });
      }
      mutation.addedNodes.forEach(observeAutoplayWrappers);
      mutation.removedNodes.forEach((node) => {
        removedRoots.push(node);
      });
    });

    removedRoots.forEach(forgetHoverVideoWrappers);
  });

  document.addEventListener("pointerover", handlePointerOver);
  document.addEventListener("pointerout", handlePointerOut);
  document.addEventListener("theme:loading:start", () => {
    stopActiveHoverVideo();
    stopAutoplayVideos(false);
  });
  document.addEventListener("theme:loading:end", () => {
    window.requestAnimationFrame(() => {
      observeAutoplayWrappers(document);
      resumeAutoplayVideos();
    });
  });
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      stopActiveHoverVideo();
      stopAutoplayVideos(false);
      return;
    }

    resumeAutoplayVideos();
  });
  window.addEventListener("blur", () => {
    stopActiveHoverVideo();
    stopAutoplayVideos(false);
  });
  window.addEventListener("focus", resumeAutoplayVideos);

  autoplayMutationObserver.observe(document.body, {
    childList: true, subtree: true, attributes: true, attributeFilter: ["hidden"]
  });
  setupAutoplayObserver();

  if (typeof hoverVideoMedia.addEventListener === "function") {
    hoverVideoMedia.addEventListener("change", (event) => {
      stopActiveHoverVideo();

      if (event.matches) {
        setupAutoplayObserver();
      } else {
        teardownAutoplayObserver();
      }
    });
  } else if (typeof hoverVideoMedia.addListener === "function") {
    hoverVideoMedia.addListener((event) => {
      stopActiveHoverVideo();

      if (event.matches) {
        setupAutoplayObserver();
      } else {
        teardownAutoplayObserver();
      }
    });
  }
})();
